import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import {
  CUSTOMER_PROFILE_REFUSAL_MESSAGES,
  customerProfileFailureMessageKey,
} from '@/components/setup/customer-profile/customer-profile-run'
import { i18n } from '@/i18n'
import { type CallableError, parseCallableError } from '@/lib/firebase/errors'
import {
  CUSTOMER_PROFILE_NAVIGATOR,
  type CustomerProfileMove,
  resumeCustomerProfileScreen,
} from '@/lib/setup/customer-profile-navigation'
import { screenIndex } from '@/lib/setup/setup-screen'
import { CONFIRM_STAMP_WAIT_MS, whenChanged } from '@/lib/setup/when-changed'
import {
  confirmCustomerProfile as repoConfirmCustomerProfile,
  isCustomerProfileRefusalReason,
  isCustomerProfileRunInProgressReason,
  startCustomerProfile as repoStartCustomerProfile,
  subscribeCustomerProfile as repoSubscribeCustomerProfile,
  subscribeLatestCustomerProfileRun as repoSubscribeLatestCustomerProfileRun,
  writeCustomerProfileSummary as repoWriteCustomerProfileSummary,
} from '@/repositories/customer-profile'
import { isOnboardingLaterStepRunningReason } from '@/repositories/setup'
import { useBrandStore } from '@/stores/brand'
import { useSetupPositionStore } from '@/stores/setup-position'
import {
  currentSetupScope,
  useLatestRun,
  useScopedSubscription,
} from '@/stores/setup-subscriptions'
import { useToastStore } from '@/stores/toast'
import {
  CUSTOMER_PROFILE_RUN_STALE_AFTER_MS,
  type CustomerProfileRefusalReason,
  type CustomerProfileRun,
  type ProfileIdentityInput,
  type ProfileRowId,
  isCustomerProfileApproved,
} from '@/types/customer-profile'
import { isError, isLoaded } from '@/types/load-state'
import { type StepConfirmation, isCurrentSince } from '@/types/onboarding-chain'
import { isRunFailed, runActivity } from '@/types/setup-run'

export type StartCustomerProfileOutcome =
  | { readonly status: 'started'; readonly runId: string }
  | { readonly status: 'refused'; readonly reason: CustomerProfileRefusalReason | undefined }

/** The toast line for a refused customer-profile call: plain words for a known reason, else the server's message. */
function describeRefusal(failure: CallableError): string {
  return isCustomerProfileRefusalReason(failure.reason)
    ? i18n.global.t(CUSTOMER_PROFILE_REFUSAL_MESSAGES[failure.reason])
    : failure.message
}

function refusalReason(failure: CallableError): CustomerProfileRefusalReason | undefined {
  return isCustomerProfileRefusalReason(failure.reason) ? failure.reason : undefined
}

export const useCustomerProfileSetupStore = defineStore('setup-customer-profile', () => {
  const runSubscription = useLatestRun<CustomerProfileRun>(
    repoSubscribeLatestCustomerProfileRun,
    CUSTOMER_PROFILE_RUN_STALE_AFTER_MS,
  )
  const profileSubscription = useScopedSubscription(repoSubscribeCustomerProfile)

  const latestRun = runSubscription.state
  const run = runSubscription.run
  const profileState = profileSubscription.state

  const calling = ref(false)
  const awaitedRunId = ref<string | null>(null)
  const startFailed = ref(false)
  let autoStartAsked = false

  const writingSummary = ref(false)
  const confirming = ref(false)

  function open(): void {
    const scope = currentSetupScope()
    if (!scope) {
      const message = i18n.global.t('common.noWorkspaceSelected')
      runSubscription.fail(message)
      profileSubscription.fail(message)
      return
    }
    runSubscription.open(scope)
    profileSubscription.open(scope)
  }

  function close(): void {
    runSubscription.close()
    profileSubscription.close()
    awaitedRunId.value = null
    startFailed.value = false
    autoStartAsked = false
  }

  const resolved = computed(
    () => latestRun.value.status !== 'loading' && profileState.value.status !== 'loading',
  )

  const readFailed = computed(() => isError(latestRun.value) || isError(profileState.value))

  const profile = computed(() => (isLoaded(profileState.value) ? profileState.value.value : null))

  const finished = computed(
    () => profile.value !== null && isCustomerProfileApproved(profile.value),
  )

  const confirmation = computed<StepConfirmation>(() => ({
    confirmed: finished.value,
    confirmedAt: profile.value?.meta.confirmedAt ?? null,
  }))

  const competitorsConfirmedAt = computed(() => {
    const brand = useBrandStore().brand
    return isLoaded(brand) ? brand.value.competitorsConfirmedAt : null
  })

  const draftCurrent = computed(
    () => run.value !== null && isCurrentSince(run.value.createdAt, competitorsConfirmedAt.value),
  )

  const currentRun = computed(() => (draftCurrent.value ? run.value : null))

  watch(run, (latest) => {
    if (latest !== null && latest.id === awaitedRunId.value) awaitedRunId.value = null
  })

  const starting = computed(() => calling.value || awaitedRunId.value !== null)

  const isWriting = runSubscription.isLive

  const activity = computed(() => runActivity(run.value))

  const draftFailed = computed(
    () =>
      !starting.value &&
      (startFailed.value || (currentRun.value !== null && isRunFailed(currentRun.value))),
  )

  const failureMessageKey = computed(() =>
    draftFailed.value
      ? customerProfileFailureMessageKey(currentRun.value, startFailed.value)
      : undefined,
  )

  function resumeIndex(): number {
    return screenIndex(
      CUSTOMER_PROFILE_NAVIGATOR.screens,
      resumeCustomerProfileScreen({
        hasProfile: profile.value !== null,
        runLive: isWriting.value,
        current: draftCurrent.value,
      }),
    )
  }

  function move(applied: CustomerProfileMove): void {
    useSetupPositionStore().move('customer-profile', CUSTOMER_PROFILE_NAVIGATOR, applied)
  }

  /** Opens a draft and reports the run to watch or the reason it was refused; `keepEdited` leaves founder edits alone. */
  async function start(keepEdited: boolean): Promise<StartCustomerProfileOutcome> {
    calling.value = true
    startFailed.value = false
    try {
      const { runId } = await repoStartCustomerProfile({ keepEdited })
      if (run.value?.id !== runId) awaitedRunId.value = runId
      return { status: 'started', runId }
    } catch (cause) {
      const parsed = parseCallableError(cause)
      if (!isCustomerProfileRunInProgressReason(parsed.reason)) {
        startFailed.value = true
        useToastStore().error(i18n.global.t('setup.errors.startFailed'), describeRefusal(parsed))
      }
      return { status: 'refused', reason: refusalReason(parsed) }
    } finally {
      calling.value = false
    }
  }

  async function writeAgain(): Promise<StartCustomerProfileOutcome> {
    const outcome = await start(true)
    if (outcome.status === 'started') move('draft-opened')
    return outcome
  }

  async function retry(): Promise<StartCustomerProfileOutcome> {
    return start(profile.value !== null)
  }

  watch(competitorsConfirmedAt, () => {
    autoStartAsked = false
  })

  watch(
    () =>
      useSetupPositionStore().position?.step === 'customer-profile' &&
      resolved.value &&
      !readFailed.value &&
      !starting.value &&
      !isWriting.value &&
      !draftCurrent.value,
    (due) => {
      if (!due || autoStartAsked) return
      autoStartAsked = true
      void start(false)
    },
    { immediate: true },
  )

  /** Saves the lines of one row, marking it edited. */
  async function saveRow(card: ProfileRowId, items: readonly string[]): Promise<boolean> {
    return useBrandStore().saveCustomerProfile({ section: 'row', card, items }, describeRefusal)
  }

  async function saveIdentity(input: ProfileIdentityInput): Promise<boolean> {
    return useBrandStore().saveCustomerProfile({ section: 'identity', ...input }, describeRefusal)
  }

  async function writeSummaryAgain(): Promise<string | null> {
    if (writingSummary.value) return null
    writingSummary.value = true
    try {
      return await repoWriteCustomerProfileSummary()
    } catch (cause) {
      const parsed = parseCallableError(cause)
      useToastStore().error(
        i18n.global.t('setup.customerProfile.errors.writeSummaryFailed'),
        describeRefusal(parsed),
      )
      return null
    } finally {
      writingSummary.value = false
    }
  }

  async function confirm(): Promise<boolean> {
    if (confirming.value) return false
    confirming.value = true
    try {
      const before = confirmation.value.confirmedAt
      await repoConfirmCustomerProfile()
      await whenChanged(() => confirmation.value.confirmedAt, before, CONFIRM_STAMP_WAIT_MS)
      return true
    } catch (cause) {
      const parsed = parseCallableError(cause)
      if (isOnboardingLaterStepRunningReason(parsed.reason)) {
        useToastStore().error(i18n.global.t('setup.locked.laterStepRunning'))
      } else {
        useToastStore().error(i18n.global.t('setup.errors.confirmFailed'), describeRefusal(parsed))
      }
      return false
    } finally {
      confirming.value = false
    }
  }

  return {
    latestRun,
    run,
    currentRun,
    draftCurrent,
    profileState,
    profile,
    isWriting,
    starting,
    startFailed,
    draftFailed,
    failureMessageKey,
    writingSummary,
    confirming,
    resolved,
    readFailed,
    finished,
    confirmation,
    activity,
    open,
    close,
    resumeIndex,
    move,
    start,
    writeAgain,
    retry,
    saveRow,
    saveIdentity,
    writeSummaryAgain,
    confirm,
  }
})
